import React, { useState, useEffect } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { Loader } from "../../../../Components/Loader/Loader";
import axios from "axios";
import { url } from "../../../../env";

// Map Creditrust color names to system-compatible values
const colorMap = {
    Green: { bg: "#E8F5E9", border: "#66BB6A", text: "#2E7D32", severity: "success" },
    Red: { bg: "#FFEBEE", border: "#EF5350", text: "#C62828", severity: "danger" },
    Yellow: { bg: "#FFF8E1", border: "#FFC107", text: "#F57F17", severity: "warning" },
    Orange: { bg: "#FFF3E0", border: "#FF9800", text: "#E65100", severity: "warning" },
    Blue: { bg: "#E3F2FD", border: "#42A5F5", text: "#1565C0", severity: "info" },
    Gray: { bg: "#F5F5F5", border: "#BDBDBD", text: "#616161", severity: "info" },
};

const getColorStyle = (color) => colorMap[color] || colorMap.Gray;

// Map State to readable label
const stateLabel = {
    Finished: "Finalizado",
    Failed: "Fallido",
    Pending: "Pendiente",
    Running: "En proceso",
};

export default function CreditrustPanel({ persona, toast, mockData, compact = false }) {
    const [creditrustData, setCreditrustData] = useState(mockData || null);
    const [loadingCreditrust, setLoadingCreditrust] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [showForceModal, setShowForceModal] = useState(false);
    const [forcing, setForcing] = useState(false);

    const canFetch = Boolean(persona?.company_id);

    const canReRegister = persona?.company_id && (
        persona?.creditrust_registered ||
        (persona?.syntage_status && persona?.buro_status)
    );

    const fetchCreditrustData = async () => {
        if (!persona?.company_id) return;

        setLoadingCreditrust(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(
                `${url}/creditrust/status/${persona.company_id}?include_history=true`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            setCreditrustData(response.data);
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Error al obtener datos de Creditrust",
                life: 3000,
            });
        } finally {
            setLoadingCreditrust(false);
        }
    };

    const handleReRegister = async () => {
        if (!persona?.company_id) return;

        setRegistering(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                `${url}/creditrust/register/${persona.company_id}`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            toast?.current?.show({
                severity: "success",
                summary: "Solicitud enviada",
                detail: "Se ha iniciado la re-extracción de datos. Este proceso puede tardar",
                life: 5000,
            });

            // Refrescar estado local después de un pequeño retraso
            setTimeout(fetchCreditrustData, 2000);

        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Error al solicitar re-extracción",
                life: 4000,
            });
        } finally {
            setRegistering(false);
        }
    };

    const handleForceReRegister = async () => {
        if (!persona?.company_id) return;

        setForcing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.post(
                `${url}/creditrust/register/${persona.company_id}`,
                {
                    flow_type: "force_syntage_external",
                    force_note: "Confirmado manualmente en UI para extraccion forzada"
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                    },
                }
            );

            toast?.current?.show({
                severity: "success",
                summary: "Extracción forzada",
                detail: "Se ha iniciado la extracción omitiendo validación de Syntage.",
                life: 5000,
            });

            setShowForceModal(false);
            setTimeout(fetchCreditrustData, 2000);

        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Error al forzar extracción",
                life: 4000,
            });
        } finally {
            setForcing(false);
        }
    };

    // Auto-fetch and polling (skip if using mock data)
    useEffect(() => {
        let intervalId;

        if (!mockData && canFetch) {
            fetchCreditrustData();
            intervalId = setInterval(fetchCreditrustData, 300000); // 5 min
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [persona?.company_id, persona?.syntage_status, persona?.buro_status, persona?.creditrust_registered]);

    // PF no utiliza Creditrust
    if (persona?.persona_subtype === "PF") {
        return (
            <Message
                severity="info"
                text="Creditrust no aplica para Persona Física (PF)."
                className="w-full"
            />
        );
    }



    const renderForceModal = () => (
        <Dialog
            header="Confirmar extracción forzada"
            visible={showForceModal}
            style={{ width: '90vw', maxWidth: '400px' }}
            onHide={() => setShowForceModal(false)}
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button label="Cancelar" icon="pi pi-times" onClick={() => setShowForceModal(false)} className="p-button-text p-button-secondary" />
                    <Button label="Confirmar y forzar" icon="pi pi-check" onClick={handleForceReRegister} className="p-button-danger" loading={forcing} />
                </div>
            }
        >
            <p className="m-0 text-color-secondary">
                Esta acción omite la validación local de Syntage. Confirma que el cliente ya está registrado externamente en Syntage (o de lo contrario fallará). ¿Deseas continuar?
            </p>
        </Dialog>
    );

    // --- Loading state ---
    if (loadingCreditrust && !creditrustData) {
        return (
            <>
                <Loader msg="Cargando datos de Creditrust..." />
                {renderForceModal()}
            </>
        );
    }

    // --- Calculate timing for pending extractions (used in multiple places) ---
    const lastRequestDate = creditrustData?.last_request?.created_at ? new Date(creditrustData.last_request.created_at) : null;
    const lastWebhookDate = creditrustData?.current?.received_at ? new Date(creditrustData.current.received_at) : null;

    const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
    const isNewerRequest = lastRequestDate && (!lastWebhookDate || (lastRequestDate.getTime() > lastWebhookDate.getTime() + 5000));

    let isPendingWebhook = false;
    let hasTimedOut = false;
    let timeElapsedString = "";

    if (isNewerRequest) {
        const diffMs = new Date() - lastRequestDate;
        if (diffMs > TWO_HOURS_MS) {
            hasTimedOut = true;
        } else {
            isPendingWebhook = true;
            const diffMins = Math.floor(diffMs / 60000);
            if (diffMins < 1) timeElapsedString = "hace menos de un minuto";
            else if (diffMins === 1) timeElapsedString = "hace 1 minuto";
            else timeElapsedString = `hace ${diffMins} minutos`;
        }
    }

    // --- No data yet ---
    if (!creditrustData || !creditrustData.current) {
        return (
            <div className="flex flex-column gap-3 p-3">
                <div className="flex align-items-center gap-2 mb-2">
                    <i className="pi pi-shield text-2xl text-orange-500" />
                    <span className="text-xl font-semibold">Creditrust</span>
                </div>
                {!persona?.syntage_status && (
                    <Message severity="warn" text="Falta: El usuario no tiene una contraseña válida para Syntage." className="w-full" />
                )}
                {!persona?.buro_status && (
                    <Message severity="warn" text="Falta: El usuario no tiene consulta de Buró válida." className="w-full" />
                )}

                {isPendingWebhook ? (
                    <div className="flex align-items-center gap-2 bg-blue-50 p-3 border-round border-blue-200" style={{ border: '1px solid' }}>
                        <i className="pi pi-spin pi-spinner text-blue-500 text-xl"></i>
                        <span className="text-sm text-blue-700 font-medium">
                            Extracción solicitada {timeElapsedString}. Esperando respuesta del webhook...
                        </span>
                    </div>
                ) : hasTimedOut ? (
                    <Message severity="error" text="La última extracción solicitada agotó el tiempo de espera (más de 2 horas) sin recibir respuesta de Creditrust." className="w-full" />
                ) : (
                    <Message severity="info" text="No hay datos de Creditrust disponibles aún para esta empresa." className="w-full" />
                )}

                <div className="flex gap-2">
                    <Button
                        label="Traer Datos"
                        icon="pi pi-download"
                        className="p-button-warning w-auto"
                        onClick={fetchCreditrustData}
                        loading={loadingCreditrust}
                        disabled={isPendingWebhook || registering || forcing}
                    />
                    {canReRegister && (
                        <Button
                            label="Volver a extraer datos"
                            icon="pi pi-sync"
                            className="p-button-secondary p-button-outlined w-auto"
                            onClick={handleReRegister}
                            loading={registering}
                            disabled={isPendingWebhook || loadingCreditrust || forcing}
                            tooltip="Esto iniciará una nueva solicitud de datos a Creditrust"
                            tooltipOptions={{ position: 'bottom' }}
                        />
                    )}
                    {!persona?.syntage_status && (
                        <Button
                            label="Forzar extracción"
                            icon="pi pi-bolt"
                            className="p-button-danger w-auto"
                            onClick={() => setShowForceModal(true)}
                            loading={forcing || registering}
                            disabled={isPendingWebhook || loadingCreditrust}
                        />
                    )}
                </div>
                {renderForceModal()}
            </div>
        );
    }

    // --- Render data ---
    const { current } = creditrustData;
    const process = current.process || {};
    const steps = process.steps || [];
    const scorePercent = process.score_maximo ? Math.round((process.score / process.score_maximo) * 100) : 0;
    // --- Compact mode (for Mesa de Control inside stepper) ---
    if (compact) {
        return (
            <div className="flex flex-column gap-2">
                {/* Actions (visible when there is data) */}
                <div className="flex justify-content-end gap-2 mb-1">
                    {isPendingWebhook ? (
                        <div className="flex align-items-center gap-2 bg-blue-50 p-2 border-round">
                            <i className="pi pi-spin pi-spinner text-blue-500"></i>
                            <span className="text-xs text-blue-700 font-medium">
                                Extracción solicitada {timeElapsedString}...
                            </span>
                        </div>
                    ) : (
                        <>
                            {canReRegister && (
                                <Button
                                    label="Volver a extraer"
                                    icon="pi pi-sync"
                                    className="p-button-outlined p-button-sm"
                                    onClick={handleReRegister}
                                    loading={registering}
                                    disabled={isPendingWebhook || loadingCreditrust}
                                    tooltip="Solicitar nueva extracción"
                                    tooltipOptions={{ position: 'left' }}
                                    style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                                />
                            )}
                            <Button
                                label="Actualizar"
                                icon="pi pi-refresh"
                                className="p-button-text p-button-sm"
                                onClick={fetchCreditrustData}
                                loading={loadingCreditrust}
                                disabled={isPendingWebhook || registering}
                                style={{ padding: '0.4rem 0.6rem', fontSize: '0.8rem' }}
                            />
                        </>
                    )}
                </div>

                {/* Compact Header + Score */}
                <div
                    className="flex align-items-center justify-content-between p-2 border-round-lg"
                    style={{
                        background: process.success
                            ? "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)"
                            : "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
                    }}
                >
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-shield text-xl" style={{ color: process.success ? '#2E7D32' : '#C62828' }} />
                        <div className="flex flex-column">
                            <span className="text-sm font-bold text-900">{process.name || "—"}</span>
                            <span className="text-xs text-500">
                                RFC: {creditrustData.rfc} · {creditrustData.company_name}
                            </span>
                        </div>
                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="text-xl font-bold" style={{ color: process.success ? "#2E7D32" : "#C62828" }}>
                            {process.score ?? "—"}<span className="text-xs font-normal text-500">/{process.score_maximo ?? "—"}</span>
                        </span>
                        <Tag
                            value={process.success ? "Aprobado" : "Rechazado"}
                            severity={process.success ? "success" : "danger"}
                            rounded
                            style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem', lineHeight: '1.2' }}
                        />
                    </div>
                </div>

                {/* Compact Steps Grid */}
                {steps.length > 0 && (
                    <div className="grid">
                        {steps.map((step, idx) => {
                            const colors = getColorStyle(step.Color);
                            return (
                                <div key={idx} className="col-6 md:col-4 lg:col-3">
                                    <div
                                        className="p-2 border-round shadow-1 h-full"
                                        style={{
                                            backgroundColor: colors.bg,
                                            borderLeft: `3px solid ${colors.border}`,
                                        }}
                                    >
                                        <div className="flex justify-content-between align-items-center mb-1">
                                            <span
                                                className="text-xs font-bold"
                                                style={{ color: colors.text }}
                                            >
                                                {step.Name}
                                            </span>
                                            <Tag
                                                value={stateLabel[step.State] || step.State}
                                                severity={colors.severity}
                                                className="text-xs"
                                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', whiteSpace: 'nowrap' }}
                                                rounded
                                            />
                                        </div>
                                        <div className="flex align-items-center justify-content-between">
                                            <span className="text-xs text-500">
                                                {step.ResultExplanation && (
                                                    <div className="text-xs text-700 font-medium  line-height-2" style={{ fontSize: '0.7rem' }}>
                                                        {step.ResultExplanation}
                                                    </div>
                                                )}
                                            </span>
                                            {step.Result && (
                                                <span
                                                    className="text-xs font-medium"
                                                    style={{ color: colors.text, fontSize: '0.7rem' }}
                                                >
                                                    {step.Result}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {renderForceModal()}
            </div>
        );
    }

    // --- Full mode (default, for sidebar) ---

    return (
        <div className="flex flex-column gap-3">
            {/* Header */}
            <div className="flex align-items-center justify-content-between">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-shield text-2xl text-orange-500" />
                    <span className="text-xl font-semibold">Creditrust</span>
                </div>
                <div className="flex gap-2">
                    {canReRegister && (
                        <Button
                            label="Volver a extraer"
                            icon="pi pi-sync"
                            className="p-button-outlined p-button-sm"
                            onClick={handleReRegister}
                            loading={registering}
                            disabled={isPendingWebhook || loadingCreditrust}
                            tooltip="Solicitar nueva extracción"
                            tooltipOptions={{ position: 'left' }}
                        />
                    )}
                    <Button
                        label="Actualizar"
                        icon="pi pi-refresh"
                        className="p-button-text p-button-sm"
                        onClick={fetchCreditrustData}
                        loading={loadingCreditrust}
                        disabled={isPendingWebhook || registering}
                    />
                </div>
            </div>

            {/* Warnings for missing requirements */}
            {!persona?.syntage_status && (
                <Message severity="warn" text="Falta: El usuario no tiene una contraseña válida para Syntage." className="w-full" />
            )}
            {!persona?.buro_status && (
                <Message severity="warn" text="Falta: El usuario no tiene consulta de Buró válida." className="w-full" />
            )}

            {/* Summary Card */}
            <Card
                className="shadow-2 border-round-lg"
                style={{
                    background: process.success
                        ? "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)"
                        : "linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)",
                }}
            >
                <div className="flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="flex flex-column gap-1">
                        <span className="text-sm font-medium text-600">Proceso</span>
                        <span className="text-lg font-bold text-900">{process.name || "—"}</span>
                        <span className="text-xs text-500">
                            RFC: {creditrustData.rfc} · {creditrustData.company_name}
                        </span>
                    </div>
                    <div className="flex flex-column align-items-center gap-1">
                        <span className="text-3xl font-bold" style={{ color: process.success ? "#2E7D32" : "#C62828" }}>
                            {process.score ?? "—"}
                        </span>
                        <span className="text-xs text-500">de {process.score_maximo ?? "—"}</span>
                        <Tag
                            value={process.success ? "Aprobado" : "Rechazado"}
                            severity={process.success ? "success" : "danger"}
                            rounded
                        />
                    </div>
                </div>
                <div className="flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                    <div className="flex flex-column">
                        <span className="text-xs font-semibold text-700">Última extracción del webhook:</span>
                        <span className="text-xs text-500">
                            {current.received_at ? lastWebhookDate.toLocaleString('es-MX', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }) : "Sin fecha recibida"}
                        </span>
                    </div>
                    {isPendingWebhook ? (
                        <div className="flex align-items-center gap-2 bg-blue-50 p-2 border-round">
                            <i className="pi pi-spin pi-spinner text-blue-500"></i>
                            <span className="text-xs text-blue-700 font-medium">
                                Extracción solicitada {timeElapsedString}. Esperando respuesta...
                            </span>
                        </div>
                    ) : registering && (
                        <Tag value="Esperando nueva extracción..." severity="info" pt={{ root: { className: 'text-xs' } }} />
                    )}
                </div>
            </Card>

            {/* Steps Grid */}
            {steps.length > 0 && (
                <>
                    <span className="text-sm font-semibold text-600 mt-2">
                        Detalle de pasos ({steps.length})
                    </span>
                    <div className="grid">
                        {steps.map((step, idx) => {
                            const colors = getColorStyle(step.Color);
                            return (
                                <div key={idx} className="col-12 md:col-6 lg:col-4">
                                    <div
                                        className="p-3 border-round-lg shadow-1 h-full"
                                        style={{
                                            backgroundColor: colors.bg,
                                            borderLeft: `4px solid ${colors.border}`,
                                        }}
                                    >
                                        <div className="flex justify-content-between align-items-start mb-2">
                                            <span
                                                className="text-sm font-bold"
                                                style={{ color: colors.text }}
                                            >
                                                {step.Name}
                                            </span>
                                            <Tag
                                                value={stateLabel[step.State] || step.State}
                                                severity={colors.severity}
                                                className="text-xs"
                                                rounded
                                            />
                                        </div>
                                        {step.ResultExplanation && (
                                            <p className="text-xs text-700 m-0 mb-2">
                                                {step.ResultExplanation}
                                            </p>
                                        )}
                                        <div className="flex align-items-center justify-content-between">
                                            <span className="text-xs text-500">
                                                Score: <strong>{step.Score ?? "—"}</strong>
                                            </span>
                                            {step.Result && (
                                                <span
                                                    className="text-xs font-medium"
                                                    style={{ color: colors.text }}
                                                >
                                                    {step.Result}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Raw JSON toggle */}
            {/*  <details className="mt-2">
                <summary className="cursor-pointer text-sm text-blue-600">Ver JSON completo</summary>
                <pre
                    className="text-xs mt-2 p-2 bg-gray-100 border-round overflow-auto"
                    style={{ maxHeight: "300px" }}
                >
                    {JSON.stringify(creditrustData, null, 2)}
                </pre>
            </details> */}

            {renderForceModal()}
        </div>
    );
}
